<template>
  <div class="learn-progress">
    <Loader cover v-if="loading"/>

    <vx-card class="mb-base" v-if="student">
      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-2">
          <span class="font-semibold">{{ $t('learn-progress.student-code') }}:</span> {{ student.code }}
        </div>
        <div class="vx-col w-full md:w-1/2 mb-2">
          <span class="font-semibold">{{ $t('learn-progress.student-name') }}:</span> {{ studentName }}
        </div>
        <div class="vx-col w-full md:w-1/2 mb-2" v-if="student.faculty">
          <span class="font-semibold">{{ $t('learn-progress.faculty') }}:</span> {{ student.faculty.nameLocal }}
        </div>
        <div class="vx-col w-full md:w-1/2 mb-2" v-if="student.curriculum">
          <span class="font-semibold">{{ $t('learn-progress.curriculum') }}:</span> {{ student.curriculum.nameLocal }}
        </div>
        <div class="vx-col w-full md:w-1/2 mb-2" v-if="student.telephone">
          <span class="font-semibold">{{ $t('learn-progress.telephone') }}:</span> {{ student.telephone }}
        </div>
      </div>
    </vx-card>

    <vx-card>
      <div v-if="failed" class="learn-progress__error text-center py-8">
        <p class="mb-4">{{ $t('learn-progress.load-failed') }}</p>
        <div class="flex justify-center">
          <vs-button @click="load" icon-pack="feather" icon="icon-refresh-cw">{{ $t('learn-progress.retry') }}</vs-button>
        </div>
      </div>

      <p v-else-if="loaded && courses.length === 0" class="learn-progress__empty text-center py-8">
        {{ $t('learn-progress.empty') }}
      </p>

      <vs-table v-else-if="loaded" :data="courses">
        <template slot="thead">
          <vs-th v-for="header in headers" :key="header">{{ $t(header) }}</vs-th>
        </template>

        <template slot-scope="{data}">
          <vs-tr v-for="(tr, index) in data" :key="tr.studentEnrollDetailId" :data="tr" class="learn-progress__course">
            <vs-td>{{ index + 1 }}</vs-td>
            <vs-td>{{ tr.subjectCode }}</vs-td>
            <vs-td>{{ tr.subjectName }}</vs-td>
            <vs-td>{{ tr.learnSpanNo }}</vs-td>
            <vs-td>{{ tr.sectionNo }}</vs-td>
            <vs-td>{{ formatTime(tr.totalLearnTime) }}</vs-td>
            <vs-td>{{ formatTime(tr.totalVdoLength) }}</vs-td>
            <vs-td>{{ tr.totalLearnPercent }}</vs-td>

            <template slot="expand">
              <p v-if="!tr.units || tr.units.length === 0" class="learn-progress__no-units p-4">
                {{ $t('learn-progress.no-units') }}
              </p>
              <table v-else class="learn-progress__units w-full">
                <thead>
                  <tr>
                    <th v-for="header in unitHeaders" :key="header">{{ $t(header) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(unit, unitIndex) in tr.units" :key="unitIndex" class="learn-progress__unit">
                    <td>{{ unit.seq }}</td>
                    <td>{{ formatTime(unit.totalLearnTime) }}</td>
                    <td>{{ formatTime(unit.totalVdoLength) }}</td>
                    <td>{{ unit.learnPercent }}</td>
                    <td>{{ testScore(unit.pretestScore, unit.pretestMaxScore, unit.pretestDone) }}</td>
                    <td>{{ testScore(unit.posttestScore, unit.posttestMaxScore, unit.posttestDone) }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
          </vs-tr>
        </template>
      </vs-table>

      <div class="learn-progress__notes mt-6 text-sm">
        <p class="font-semibold">{{ $t('learn-progress.section-note.title') }}</p>
        <ul class="ml-4">
          <li>{{ $t('learn-progress.section-note.plan') }}</li>
          <li>{{ $t('learn-progress.section-note.missed-1') }}</li>
          <li>{{ $t('learn-progress.section-note.missed-2') }}</li>
          <li>{{ $t('learn-progress.section-note.other') }}</li>
        </ul>
        <p class="mt-2">{{ $t('learn-progress.delay-note') }}</p>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { apolloClient } from '@/setup/apollo-setup';
import { GET_MY_LEARN_PROGRESS } from '@/queries/modules/student-enroll/learn-progress';
import { formatSecondToHHMMSS } from '@/utils/format';
import { showLocalNotification, notificationType } from '@/utils/notification';
import { formatTestScore } from '@/utils/test-score';

export default {
  name: 'LearnProgress',
  components: {
    Loader: () => import('@/components/common-ui/loader-block/index.vue'),
  },
  data() {
    return {
      loading: false,
      loaded: false,
      failed: false,
      student: null,
      courses: [],
      headers: [
        'learn-progress.col-no',
        'learn-progress.col-subject-code',
        'learn-progress.col-subject-name',
        'learn-progress.col-learn-span',
        'learn-progress.col-section',
        'learn-progress.col-learn-time',
        'learn-progress.col-total-time',
        'learn-progress.col-percent',
      ],
      unitHeaders: [
        'learn-progress.col-unit',
        'learn-progress.col-learn-time',
        'learn-progress.col-total-time',
        'learn-progress.col-percent',
        'learn-progress.col-pretest',
        'learn-progress.col-posttest',
      ],
    };
  },
  computed: {
    studentName() {
      const s = this.student;
      const prefix = s.prefix ? s.prefix.nameLocal || '' : '';
      return `${prefix}${s.firstNameLocal || ''} ${s.lastNameLocal || ''}`.trim();
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatTime(seconds) {
      return formatSecondToHHMMSS(seconds || 0);
    },
    testScore(score, maxScore, done) {
      return formatTestScore(score, maxScore, done, this.$t('learn-progress.not-taken'));
    },
    async load() {
      this.loading = true;
      this.failed = false;
      try {
        // Learn time is written in the background, so a cached answer would
        // hide what the student just watched.
        const { data } = await apolloClient.query({ query: GET_MY_LEARN_PROGRESS, fetchPolicy: 'network-only' });
        this.student = data.getStudentByCurrentUser;
        this.courses = data.getMyLearnProgress || [];
        this.loaded = true;
      } catch (e) {
        this.failed = true;
        showLocalNotification(this.$vs, this.$t('learn-progress.title'), this.$t('learn-progress.load-failed'),
          notificationType.ERROR);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.learn-progress__units {
  border-collapse: collapse;

  th,
  td {
    padding: 0.5rem 1rem;
    text-align: left;
  }

  thead tr {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
}
</style>
